import { hash } from 'immutable';
import { type Action, combineActions, handleActions } from 'redux-actions';

import { coreActions } from 'app-components/flux/core-actions';

import { resourceStoreTypes, resourceUndoUpserters, resourceUpserters } from '../core';
import * as actions from './reportConfigurationsActions';
import type { ReportConfiguration, ReportConfigurationProps, ReportNameType } from './reportConfigurationsTypes';
import { adjustReportConfiguration } from './utils/reportFieldAvailability';

const QcsSyncReportConfigurationStoreFactory =
  resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<ReportConfigurationProps>();

const reportConfigurationsReducer = handleActions<
  resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps>,
  | coreActions.SanitizeAction
  | ReturnType<typeof actions.createReportConfiguration>
  | ReturnType<typeof actions.createReportConfigurationSuccess>
  | ReturnType<typeof actions.updateReportConfiguration>
  | ReturnType<typeof actions.updateReportConfigurationSuccess>
  | ReturnType<typeof actions.deleteReportConfiguration>
  | ReturnType<typeof actions.deleteReportConfigurationSuccess>
  | ReturnType<typeof actions.getReportConfigurations>
  | ReturnType<typeof actions.getReportConfigurationsSuccess>
  | ReturnType<typeof actions.getReportConfigurationsFailure>
  | ReturnType<typeof actions.openReportConfigurationSuccess>
  | ReturnType<typeof actions.saveReportConfigurationSuccess>
  | ReturnType<typeof actions.deleteSavedReportConfigurationSuccess>
  | ReturnType<typeof actions.renameReportConfigurationSuccess>
>(
  {
    [coreActions.SANITIZE]: (state) =>
      state.set(
        'resourcesById',
        state.resourcesById.map((resource) =>
          resource.merge({
            name: hash(resource.name).toString() as ReportNameType,
          }),
        ),
      ),

    [actions.createReportConfiguration.type]: (
      state,
      { payload: reportConfiguration }: Action<actions.REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD>,
    ): resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps> =>
      resourceUpserters.upsertResource(state, reportConfiguration, true, false, undefined, true),
    [actions.createReportConfigurationSuccess.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.deleteReportConfiguration, undefined, true),

    [actions.updateReportConfiguration.type]: (state, action) => {
      const reportConfiguration = action.payload as ReportConfiguration;

      // Only apply adjustments if explicitly requested in action meta props
      const shouldAdjust = action.meta?.props?.adjustReportConfiguration;
      const configToUse = shouldAdjust
        ? adjustReportConfiguration(reportConfiguration, action.meta?.props?.adjustProps)
        : reportConfiguration;

      // Create new action with potentially adjusted config
      const finalAction = { ...action, payload: configToUse };

      return resourceUndoUpserters.upsertResourceSetupUndo(
        state,
        finalAction,
        actions.updateReportConfiguration,
        undefined,
        true,
      );
    },
    [actions.updateReportConfigurationSuccess.type]: (state, { payload: reportConfiguration }) =>
      resourceUpserters.upsertResource(state, reportConfiguration, true, false, undefined, true),

    [actions.deleteReportConfiguration.type]: (state, action) =>
      resourceUndoUpserters.upsertResourceSetupUndo(state, action, actions.createReportConfiguration, undefined, true),
    [actions.deleteReportConfigurationSuccess.type]: (state, { payload: reportConfiguration }) =>
      resourceUpserters.upsertResource(state, reportConfiguration, true, false, undefined, true),

    [actions.openReportConfigurationSuccess.type]: (
      state,
      { payload: reportConfiguration }: Action<actions.REPORT_CONFIGURATIONS_RESOURCE_PAYLOAD>,
    ): resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps> =>
      resourceUpserters.upsertResource(state, reportConfiguration, true, false, undefined, true),
    [actions.saveReportConfigurationSuccess.type]: (
      state,
      { payload: configs }: Action<actions.REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD>,
    ): resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps> =>
      configs.reduce((s, config) => resourceUpserters.upsertResource(s, config, false, false, undefined, true), state),
    [actions.deleteSavedReportConfigurationSuccess.type]: (
      state,
      { payload: configs }: Action<actions.REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD>,
    ): resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps> =>
      configs.reduce((s, config) => resourceUpserters.upsertResource(s, config, true, false, undefined, true), state),
    [actions.renameReportConfigurationSuccess.type]: (
      state,
      { payload: configs }: Action<actions.REPORT_CONFIGURATIONS_RESOURCES_PAYLOAD>,
    ): resourceStoreTypes.QcsSyncResourceStore<ReportConfigurationProps> =>
      configs.reduce((s, config) => resourceUpserters.upsertResource(s, config, true, false, undefined, true), state),

    [actions.getReportConfigurations.type]: (state) => state.set('isLoading', true),
    [actions.getReportConfigurationsSuccess.type]: (state, { payload: data }) =>
      resourceUpserters.upsertResources(state, data, undefined, true),
    [actions.getReportConfigurationsFailure.type]: (state, { payload: error }) =>
      resourceUpserters.completeWithError(state, error),

    [combineActions(
      actions.createReportConfigurationFailure.type,
      actions.updateReportConfigurationFailure.type,
      actions.deleteReportConfigurationFailure.type,
      actions.openReportConfigurationFailure.type,
      actions.saveReportConfigurationFailure.type,
      actions.deleteSavedReportConfigurationFailure.type,
      actions.renameReportConfigurationFailure.type,
    ).toString()]: (state: any, { payload: error }: any) => {
      return resourceUpserters.resetResources(state, error);
    },
  },
  QcsSyncReportConfigurationStoreFactory(),
);

export default reportConfigurationsReducer;
